import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Paper,
  InputLabel,
} from "@mui/material";
import { QEStrategydataFlowContext } from "../../../main-pages/QEStrategyMainPage";
import QEStrategyCommonButton from "../qe-strategy-homepage/CommonButton";
import { usePrefillForm } from "../../../../utils/qe-strategy-utils/usePreFillForm";

const ApplicationArchitectureAndDataIntegration = ({ onBack, onNext }) => {
  // Selects
  const [applicationType, setApplicationType] = useState("");
  const [deploymentModel, setDeploymentModel] = useState("");
  const [deploymentFrequency, setDeploymentFrequency] = useState("");
  const [containerization, setContainerization] = useState("");
  const [cloudProvider, setCloudProvider] = useState("");
  const [usesRag, setUsesRag] = useState("");
  const [handlesSensitiveData, setHandlesSensitiveData] = useState("");
  const [dataVolume, setDataVolume] = useState("");
  const [apiIntegration, setApiIntegration] = useState("");
  const [eventSystems, setEventSystems] = useState("");
  const {
    applicationArchitectureAndDataIntegration,
    setApplicationArchitectureAndDataIntegration,
    dataFromFileUploads,
  } = useContext(QEStrategydataFlowContext);

  // Radio Groups
  const [systemType, setSystemType] = useState(""); // Core/Supporting
  const [envSync, setEnvSync] = useState(""); // Synchronized / Drift-prone
  const [dataLineage, setDataLineage] = useState(""); // Yes / No / Partially

  // Selects
  const handleApplicationTypeChange = (e) => setApplicationType(e.target.value);
  const handleDeploymentModelChange = (e) => setDeploymentModel(e.target.value);
  const handleDeploymentFrequencyChange = (e) =>
    setDeploymentFrequency(e.target.value);
  const handleContainerizationChange = (e) =>
    setContainerization(e.target.value);
  const handleCloudProviderChange = (e) => setCloudProvider(e.target.value);
  const handleUsesRagChange = (e) => setUsesRag(e.target.value);
  const handleHandlesSensitiveDataChange = (e) =>
    setHandlesSensitiveData(e.target.value);
  const handleDataVolumeChange = (e) => setDataVolume(e.target.value);
  const handleApiIntegrationChange = (e) => setApiIntegration(e.target.value);
  const handleEventSystemsChange = (e) => setEventSystems(e.target.value);

  // Radio Groups
  const handleSystemTypeChange = (e) => setSystemType(e.target.value);
  const handleEnvSyncChange = (e) => setEnvSync(e.target.value);
  const handleDataLineageChange = (e) => setDataLineage(e.target.value);

  useEffect(() => {
    if (applicationArchitectureAndDataIntegration) {
      setApplicationType(
        applicationArchitectureAndDataIntegration.applicationType || ""
      );
      setDeploymentModel(
        applicationArchitectureAndDataIntegration.deploymentModel || ""
      );
      setDeploymentFrequency(
        applicationArchitectureAndDataIntegration.deploymentFrequency || ""
      );
      setContainerization(
        applicationArchitectureAndDataIntegration.containerization || ""
      );
      setCloudProvider(
        applicationArchitectureAndDataIntegration.cloudProvider || ""
      );
      setUsesRag(applicationArchitectureAndDataIntegration.usesRag || "");
      setHandlesSensitiveData(
        applicationArchitectureAndDataIntegration.handlesSensitiveData || ""
      );
      setDataVolume(applicationArchitectureAndDataIntegration.dataVolume || "");
      setApiIntegration(
        applicationArchitectureAndDataIntegration.apiIntegration || ""
      );
      setEventSystems(
        applicationArchitectureAndDataIntegration.eventSystems || ""
      );
      setSystemType(applicationArchitectureAndDataIntegration.systemType || "");
      setEnvSync(applicationArchitectureAndDataIntegration.envSync || "");
      setDataLineage(
        applicationArchitectureAndDataIntegration.dataLineage || ""
      );
    }
  }, [applicationArchitectureAndDataIntegration]);

  // Application and Architecture Data Object
  const applicationArchitectureAndDataIntegrationDataObject = {
    applicationType,
    deploymentModel,
    deploymentFrequency,
    containerization,
    cloudProvider,
    usesRag,
    handlesSensitiveData,
    dataVolume,
    apiIntegration,
    eventSystems,
    systemType,
    envSync,
    dataLineage,
  };

  const handleNext = () => {
    setApplicationArchitectureAndDataIntegration(
      applicationArchitectureAndDataIntegrationDataObject
    );
    onNext();
  };

  const handleBack = () => {
    // Also store latest values before going back
    setApplicationArchitectureAndDataIntegration(
      applicationArchitectureAndDataIntegrationDataObject
    );
    onBack();
  };

  const [applicationTypeOptions, setApplicationTypeOptions] = useState([
    "Web",
    "Mobile",
    "API",
    "Batch",
    "LLM",
    "IOT",
    "Desktop",
    "Embedded",
  ]);

  const [deploymentModelOptions, setDeploymentModelOptions] = useState([
    "On-Premise",
    "Cloud",
    "Hybrid",
    "Edge",
  ]);

  const [deploymentFrequencyOptions, setDeploymentFrequencyOptions] = useState([
    "Weekly",
    "Monthly",
    "Quarterly",
    "Ad hoc",
  ]);

  const [containerizationOptions, setContainerizationOptions] = useState([
    "Containerized",
    "Serverless",
    "Neither",
  ]);

  const [cloudProviderOptions, setCloudProviderOptions] = useState([
    "AWS",
    "Azure",
    "Google Cloud",
    "None",
  ]);

  const [ragOptions, setRagOptions] = useState([
    "RAG",
    "Vector Databases",
    "Memory/Embedding Stores",
    "None",
  ]);

  const [sensitiveDataOptions, setSensitiveDataOptions] = useState([
    "PII",
    "Financial",
    "Health",
    "None",
  ]);

  const [dataVolumeOptions, setDataVolumeOptions] = useState([
    "High-Realtime",
    "High – Batch",
    "Medium – Realtime",
    "Medium – Batch",
    "Low – Realtime",
    "Low – Batch",
  ]);

  const [apiIntegrationOptions, setApiIntegrationOptions] = useState([
    "Internal",
    "External",
    "Third-Party",
    "Combination",
  ]);

  const [eventSystemOptions, setEventSystemOptions] = useState([
    "Kafka",
    "Webhooks",
    "None",
    "Combination (Kafka + Webhooks)",
  ]);

  const questionToFieldMap = {
    "1. What is the application type?": "applicationType",
    "2. Is the system monolithic, microservice-based, or event-driven?":
      "systemType",
    "3. What is the deployment model?": "deploymentModel",
    "4. What is the current deployment frequency?": "deploymentFrequency",
    "5. Is the application containerized or serverless?": "containerization",
    "6. Which cloud provider (if any) is used?": "cloudProvider",
    "7. Does the application use RAG, vector databases, or memory/embedding stores?":
      "usesRag",
    "8. Does the system handle sensitive data?": "handlesSensitiveData",
    "9. What is the volume and frequency of data handled?": "dataVolume",
    "10. Are APIs internal, external, or third-party integrated?":
      "apiIntegration",
    "11. Are streaming/event-based systems involved?": "eventSystems",
    "12. Are the test/stage/prod environments synchronized or drift-prone?":
      "envSync",
    "13. Is data lineage tracked during test validation?": "dataLineage",
  };

  usePrefillForm(
    dataFromFileUploads,
    questionToFieldMap,
    {
      applicationType: setApplicationType,
      systemType: setSystemType,
      deploymentModel: setDeploymentModel,
      deploymentFrequency: setDeploymentFrequency,
      containerization: setContainerization,
      cloudProvider: setCloudProvider,
      usesRag: setUsesRag,
      handlesSensitiveData: setHandlesSensitiveData,
      dataVolume: setDataVolume,
      apiIntegration: setApiIntegration,
      eventSystems: setEventSystems,
      envSync: setEnvSync,
      dataLineage: setDataLineage,
    },
    null, // since  individual useStates being used, no `setFormValues`
    {
      applicationType: {
        options: applicationTypeOptions,
        setOptions: setApplicationTypeOptions,
      },
      deploymentModel: {
        options: deploymentModelOptions,
        setOptions: setDeploymentModelOptions,
      },
      deploymentFrequency: {
        options: deploymentFrequencyOptions,
        setOptions: setDeploymentFrequencyOptions,
      },
      containerization: {
        options: containerizationOptions,
        setOptions: setContainerizationOptions,
      },
      cloudProvider: {
        options: cloudProviderOptions,
        setOptions: setCloudProviderOptions,
      },
      usesRag: { options: ragOptions, setOptions: setRagOptions },
      handlesSensitiveData: {
        options: sensitiveDataOptions,
        setOptions: setSensitiveDataOptions,
      },
      dataVolume: {
        options: dataVolumeOptions,
        setOptions: setDataVolumeOptions,
      },
      apiIntegration: {
        options: apiIntegrationOptions,
        setOptions: setApiIntegrationOptions,
      },
      eventSystems: {
        options: eventSystemOptions,
        setOptions: setEventSystemOptions,
      },
    }
  );

  return (
    <Box
      sx={{
        width: {
          xs: "100%", // full width on small screens
          sm: "100%",
          md: "1030px", // fixed width on medium and above
        },
        // height: 800
        mx: "auto", // center horizontally
        mt: { xs: 5, sm: 8, md: 3 },
        mb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
        // side padding only for smaller screens
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: 20,
            mb: -1,
            position: "relative",
            top: "-14px",
          }}
        >
          Application Architecture, Data & Integrations
        </Typography>

        <Box
          sx={{
            height: {
              xs: "100%", // full width on small screens
              sm: "100%",
              md: "850px", // fixed width on medium and above
            },
          }}
        >
          <Grid container spacing={3} sx={{ minHeight: 840 }}>
            {/* Application Type */}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel
                  shrink
                  id="application-type-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  What is the application type?
                </InputLabel>
                <Select
                  labelId="application-type-label"
                  value={applicationType}
                  onChange={handleApplicationTypeChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // limits height → scrollbar
                        width: 300, // match width of select box
                      },
                    },
                    disablePortal: false, // keeps floating on body
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false, // prevent upward flip
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {applicationTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Core / Supporting */}
            <Grid item xs={12}>
              <FormControl>
                <FormLabel>
                  Is the system monolithic, microservice-based, or event-driven?
                </FormLabel>
                <RadioGroup
                  row
                  value={systemType}
                  onChange={handleSystemTypeChange}
                >
                  <FormControlLabel
                    value="Monolithic"
                    control={<Radio />}
                    label="Monolithic"
                  />
                  <FormControlLabel
                    value="Microservice-based"
                    control={<Radio />}
                    label="Microservice-based"
                  />
                  <FormControlLabel
                    value="Event-driven"
                    control={<Radio />}
                    label="Event-driven"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Deployment Model */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-13px" }}
              >
                <InputLabel
                  shrink
                  id="deployment-type-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  What is the deployment model?
                </InputLabel>
                <Select
                  labelId="deployment-type-label"
                  value={deploymentModel}
                  onChange={handleDeploymentModelChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {deploymentModelOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Deployment Frequency */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-13px" }}
              >
                <InputLabel
                  shrink
                  id="deployment-frequency-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  What is the current deployment frequency?
                </InputLabel>
                <Select
                  labelId="deployment-frequency-label"
                  value={deploymentFrequency}
                  onChange={handleDeploymentFrequencyChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {deploymentFrequencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Containerized / Serverless */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-21px" }}
              >
                <InputLabel
                  shrink
                  id="application-containerized-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Is the application containerized or serverless?
                </InputLabel>
                <Select
                  labelId="application-containerized-label"
                  value={containerization}
                  onChange={handleContainerizationChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {containerizationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Cloud Provider */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-21px" }}
              >
                <InputLabel
                  shrink
                  id="cloud-provider-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Which cloud provider (if any) is used?
                </InputLabel>
                <Select
                  labelId="cloud-provider-label"
                  value={cloudProvider}
                  onChange={handleCloudProviderChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {cloudProviderOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* RAG / Vector DB */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-26px" }}
              >
                <InputLabel
                  shrink
                  id="embedding-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Does the application use RAG, vector databases, or
                  memory/embedding stores?
                </InputLabel>
                <Select
                  labelId="embedding-label"
                  value={usesRag}
                  onChange={handleUsesRagChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {ragOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sensitive Data */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-26px" }}
              >
                <InputLabel
                  shrink
                  id="sensitive-data-handling-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Does the system handle sensitive data?
                </InputLabel>
                <Select
                  labelId="sensitive-data-handling-label"
                  value={handlesSensitiveData}
                  onChange={handleHandlesSensitiveDataChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {sensitiveDataOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Data Volume */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-31px" }}
              >
                <InputLabel
                  shrink
                  id="volume-frequency-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  What is the volume and frequency of data handled?
                </InputLabel>
                <Select
                  labelId="volume-frequency-label"
                  value={dataVolume}
                  onChange={handleDataVolumeChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {dataVolumeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* API Integration */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-31px" }}
              >
                <InputLabel
                  shrink
                  id="API-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Are APIs internal, external, or third-party integrated?
                </InputLabel>
                <Select
                  labelId="API-label"
                  value={apiIntegration}
                  onChange={handleApiIntegrationChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {apiIntegrationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Streaming/Event Systems */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ position: "relative", top: "-36px" }}
              >
                <InputLabel
                  shrink
                  id="streaming-label"
                  sx={{
                    background: "#fff",
                    px: 0.5,
                    mb: 0.5,
                  }}
                >
                  Are streaming/event-based systems involved?
                </InputLabel>
                <Select
                  labelId="streaming-label"
                  value={eventSystems}
                  onChange={handleEventSystemsChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select</span>;
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // ✅ scrollbar if too tall
                        width: 300, // ✅ fixed width 300px
                      },
                    },
                    disablePortal: false,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PopperProps: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                  }}
                >
                  {eventSystemOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl sx={{ position: "relative", top: "-37px" }}>
                <FormLabel>
                  Are the test/stage/prod environments synchronized or
                  drift-prone?
                </FormLabel>
                <RadioGroup row value={envSync} onChange={handleEnvSyncChange}>
                  <FormControlLabel
                    value="Synchronized"
                    control={<Radio />}
                    label="Synchronized"
                  />
                  <FormControlLabel
                    value="Drift-prone"
                    control={<Radio />}
                    label="Drift-prone"
                  />
                  <FormControlLabel
                    value="Partially synchronized"
                    control={<Radio />}
                    label="Partially synchronized"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                sx={{
                  position: "relative",
                  top: "-40px",
                  borderBottom: "20px",
                }}
              >
                <FormLabel>
                  Is data lineage tracked during test validation?
                </FormLabel>
                <RadioGroup
                  row
                  value={dataLineage}
                  onChange={handleDataLineageChange}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                  <FormControlLabel
                    value="Partially"
                    control={<Radio />}
                    label="Partially"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <QEStrategyCommonButton handleBack={handleBack} handleNext={handleNext} />
    </Box>
  );
};

export default ApplicationArchitectureAndDataIntegration;